// frontend/src/pages/IcosahedronPage.jsx
import React, { useState, useEffect, useRef } from "react";
import Icosahedron3D from "../components/icosahedron3D";

// Intervalle de rafraîchissement dynamique en ms
const DYN_ENTROPY_INTERVAL = 150;

export default function IcosahedronPage() {
  // États entropies
  const [entropyUI, setEntropyUI] = useState(null);
  const [entropyDyn, setEntropyDyn] = useState(null);
  const [loadingUI, setLoadingUI] = useState(true);
  const [loadingDyn, setLoadingDyn] = useState(true);

  // Contrôles Icosaèdre
  const [running, setRunning] = useState(true);
  const [chaos, setChaos] = useState(false);
  const [glow, setGlow] = useState(true);

  const [maxSpeedEnvPct, setMaxSpeedEnvPct] = useState(50);
  const [maxSpeedCorePct, setMaxSpeedCorePct] = useState(50);
  const [pulseSpeedPct, setPulseSpeedPct] = useState(50);
  const [flashRatePct, setFlashRatePct] = useState(30);

  // Ref vers le canvas 3D
  const canvasRef = useRef(null);

  // --- Entropie UI (curseurs) ---
  const debounceTimeout = useRef(null);
  useEffect(() => {
    setLoadingUI(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    const controller = new AbortController();

    debounceTimeout.current = setTimeout(() => {
      fetch("/api/geometry/icosahedron/entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          running,
          chaos,
          glow,
          max_speed_env: maxSpeedEnvPct / 100,
          max_speed_core: maxSpeedCorePct / 100,
          pulse_speed: pulseSpeedPct / 100,
          flash_rate: flashRatePct / 100,
        }),
        signal: controller.signal,
      })

        .then(async (r) => {
          const cType = r.headers.get("content-type");
          if (cType && cType.includes("application/json")) {
            return r.json();
          }
          throw new Error("UI Entropy response is not JSON");
        })
        .then((data) => {
          if (data && typeof data.entropy === "number") {
            setEntropyUI(data.entropy);
          }
        })
        .catch((e) => {
          // console.warn(e); 
          setEntropyUI(null);
        })
        .finally(() => setLoadingUI(false));
    }, 300);

    return () => {
      clearTimeout(debounceTimeout.current);
      controller.abort();
    };
  }, [running, chaos, glow, maxSpeedEnvPct, maxSpeedCorePct, pulseSpeedPct, flashRatePct]);

  // --- Entropie dynamique (état réel du canvas) ---
  useEffect(() => {
    setLoadingDyn(true);
    let intervalId = null;

    function sendDynEntropySnapshot() {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.__oracleSnapshot) return;

      const snapshot = canvas.__oracleSnapshot();
      const { positions, colors, velocities } = snapshot;

      fetch("/api/geometry/icosahedron/dyn_entropy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ positions, colors, velocities }),
      })
        .then(async (r) => {
          const cType = r.headers.get("content-type");
          if (cType && cType.includes("application/json")) {
            return r.json();
          }
          throw new Error("Dyn Entropy response is not JSON");
        })
        .then((data) => {
          if (data && typeof data.entropy === "number") {
            console.log("🔹 Entropie dynamique calculée (backend):", data.entropy);
            setEntropyDyn(data.entropy);
          }
        })
        .catch(() => setEntropyDyn(null))
        .finally(() => setLoadingDyn(false));
    }

    intervalId = setInterval(sendDynEntropySnapshot, DYN_ENTROPY_INTERVAL);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Score total (somme brute des deux, sans plafonnement)
  const entropyTotal =
    entropyUI !== null && entropyDyn !== null
      ? (entropyUI + entropyDyn).toFixed(4)
      : "--";


  useEffect(() => {
    if (entropyUI !== null && entropyDyn !== null) {
      console.log("🔹 Score Total mis à jour:", entropyUI + entropyDyn);
    }
  }, [entropyUI, entropyDyn]);

  return (
    <div
      className="page-container"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <h1>Icosahedron Visualizer</h1>

      {/* Bloc entropies */}
      <div style={{ display: "flex", gap: 36, marginBottom: 22 }}>
        <div className="card" style={{ minWidth: 170 }}>
          <h2 style={{ marginBottom: 6 }}>Entropie UI</h2>
          <div style={{ fontSize: "1.2rem", color: "#03dac6", minHeight: 32 }}>
            {loadingUI ? "⏳" : entropyUI !== null ? entropyUI.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 170 }}>
          <h2 style={{ marginBottom: 6, color: "#ff0266" }}>Entropie Dynamique</h2>
          <div style={{ fontSize: "1.2rem", color: "#ff0266", minHeight: 32 }}>
            {loadingDyn ? "⏳" : entropyDyn !== null ? entropyDyn.toFixed(4) : "❌"}
          </div>
        </div>
        <div className="card" style={{ minWidth: 180 }}>
          <h2 style={{ marginBottom: 6, color: "#bb86fc" }}>Score Total</h2>
          <div style={{ fontSize: "1.2rem", color: "#bb86fc", minHeight: 32 }}>
            {entropyTotal}
          </div>
        </div>
      </div>

      {/* Contrôles et canvas */}
      <div style={{ display: "flex", alignItems: "center", gap: "28px" }}>
        {/* Contrôles */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            minWidth: 260,
          }}
        >
          <button className="generate-button" onClick={() => setRunning((p) => !p)}>
            {running ? "⏸ Stop Animation" : "▶ Start Animation"}
          </button>

          <button
            onClick={() => setChaos((p) => !p)}
            style={{
              padding: "12px 18px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: chaos ? "#ff0266" : "#03dac6",
              color: "#000",
              fontWeight: "bold",
            }}
          >
            {chaos ? "Désactiver Chaos" : "Activer Chaos"}
          </button>

          <button
            onClick={() => setGlow((p) => !p)}
            style={{
              padding: "12px 18px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor: glow ? "#ffd600" : "#666",
              color: "#000",
              fontWeight: "bold",
            }}
          >
            {glow ? "Désactiver Glow" : "Activer Glow"}
          </button>

          <div className="input-group">
            <label>Vitesse enveloppe : {maxSpeedEnvPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={maxSpeedEnvPct}
              onChange={(e) => setMaxSpeedEnvPct(Number(e.target.value))}
            />
          </div>
          <div className="input-group">
            <label>Vitesse noyau : {maxSpeedCorePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={maxSpeedCorePct}
              onChange={(e) => setMaxSpeedCorePct(Number(e.target.value))}
            />
          </div>
          <div className="input-group">
            <label>Pulsation : {pulseSpeedPct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={pulseSpeedPct}
              onChange={(e) => setPulseSpeedPct(Number(e.target.value))}
            />
          </div>
          <div className="input-group">
            <label>Flash : {flashRatePct}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={flashRatePct}
              onChange={(e) => setFlashRatePct(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Canvas 3D pur sans double cadre */}
        <Icosahedron3D
          ref={canvasRef}
          running={running}
          chaosMode={chaos}
          glow={glow}
          maxSpeedEnvPct={maxSpeedEnvPct}
          maxSpeedCorePct={maxSpeedCorePct}
          pulseSpeedPct={pulseSpeedPct}
          flashRatePct={flashRatePct}
          width={600}
          height={480}
        />
      </div>
    </div>
  );
}
